import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { LogOut, ArrowLeft, Eye, Calendar, User, Building } from 'lucide-react';
import JobOfferDetailModal from './JobOfferDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const RecrutementsGeneraux = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [jobOffers, setJobOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchJobOffers();
  }, []);

  const fetchJobOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers?exclude_horizon=true`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const offers = await response.json();
        setJobOffers(offers);
      } else {
        toast.error('Erreur lors du chargement des offres');
      }
    } catch (error) {
      console.error('Error fetching job offers:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBackToRH = () => {
    navigate('/rh');
  };

  const handleOfferClick = (offer) => {
    if (offer.status === 'en_cours') {
      // Navigate to detail page for "en_cours" offers
      navigate(`/rh/offer-detail/${offer.id}`);
    } else if (offer.status === 'en_attente') {
      // Open modal for "en_attente" offers
      setSelectedOffer(offer);
      setShowDetailModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowDetailModal(false);
    setSelectedOffer(null);
  };

  const handleOfferUpdated = () => {
    setShowDetailModal(false);
    setSelectedOffer(null);
    fetchJobOffers(); // Refresh the list
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifiée';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Filter offers
  const offersEnCours = jobOffers.filter(offer => offer.status === 'en_cours');
  const offersEnAttente = jobOffers.filter(offer => offer.status === 'en_attente');

  // Company logos
  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  return (
    <div className="recruitments-page">
      {/* Header */}
      <header className="rh-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={handleBackToRH}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos['Prysme']}
            alt="Logo Prysme"
            className="rh-header-logo"
          />
        </div>
        <h1>Recrutements Généraux</h1>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="rh-main">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Tabs defaultValue="en_cours" className="recruitment-tabs">
            <TabsList className="recruitment-tabs-list">
              <TabsTrigger value="en_cours" className="recruitment-tab-trigger">
                Offres en cours
                <span className="recruitment-tab-counter">{offersEnCours.length}</span>
              </TabsTrigger>
              <TabsTrigger value="en_attente" className="recruitment-tab-trigger">
                Offres en attente
                <span className="recruitment-tab-counter">{offersEnAttente.length}</span>
              </TabsTrigger>
            </TabsList>

            {/* Tab Content - Offres en cours */}
            <TabsContent value="en_cours" className="recruitment-tab-content">
              <div className="recruitment-grid-compact">
                {offersEnCours.map((offer) => (
                  <Card 
                    key={offer.id}
                    className="recruitment-card-compact cursor-pointer group"
                    onClick={() => handleOfferClick(offer)}
                  >
                    <CardContent className="recruitment-card-compact-content">
                      <div className="recruitment-card-compact-header">
                        <img
                          src={companyLogos[offer.company]}
                          alt={`Logo ${offer.company}`}
                          className="recruitment-card-compact-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <Eye className="recruitment-card-compact-icon" />
                      </div>
                      <h3 className="recruitment-card-compact-title">
                        {offer.company} : {offer.title}
                      </h3>
                      <div className="recruitment-card-compact-info">
                        <div className="recruitment-card-compact-info-item">
                          <User size={14} />
                          <span>{offer.creator_name || 'Inconnu'}</span>
                        </div>
                        <div className="recruitment-card-compact-info-item">
                          <Calendar size={14} />
                          <span>Créée le {formatDate(offer.created_at)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {offersEnCours.length === 0 && (
                  <div className="empty-state-compact">
                    <p>Aucune offre en cours</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Tab Content - Offres en attente */}
            <TabsContent value="en_attente" className="recruitment-tab-content">
              <div className="recruitment-grid-compact">
                {offersEnAttente.map((offer) => (
                  <Card 
                    key={offer.id}
                    className="recruitment-card-compact recruitment-card-compact-pending cursor-pointer group"
                    onClick={() => handleOfferClick(offer)}
                  >
                    <CardContent className="recruitment-card-compact-content">
                      <div className="recruitment-card-compact-header">
                        <img
                          src={companyLogos[offer.company]}
                          alt={`Logo ${offer.company}`}
                          className="recruitment-card-compact-logo"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <Eye className="recruitment-card-compact-icon" />
                      </div>
                      <h3 className="recruitment-card-compact-title">
                        {offer.company} : {offer.title}
                      </h3>
                      <div className="recruitment-card-compact-info">
                        <div className="recruitment-card-compact-info-item">
                          <User size={14} />
                          <span>{offer.creator_name || 'Inconnu'}</span>
                        </div>
                        <div className="recruitment-card-compact-info-item">
                          <Calendar size={14} />
                          <span>Créée le {formatDate(offer.created_at)}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {offersEnAttente.length === 0 && (
                  <div className="empty-state-compact">
                    <p>Aucune offre en attente</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* Job Offer Detail Modal for pending offers */}
      {showDetailModal && selectedOffer && (
        <JobOfferDetailModal
          isOpen={showDetailModal}
          onClose={handleCloseModal}
          onOfferUpdated={handleOfferUpdated}
          offer={selectedOffer}
        />
      )}
    </div>
  );
};

export default RecrutementsGeneraux;